import React, { useContext, useEffect, useState } from 'react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { SessionContext } from 'contexts/session';

import AppReadyLoading from 'components/loadings/AppReadyLoading';
import Notification from 'components/notification';
import HeartbeatsV2FlagSync from './HeartbeatsV2FlagSync';

interface LaunchDarklyProviderProps {
  clientSideID: string;
  context?: {
    kind: string;
    user?: {
      key: string;
      name: string;
      email: string;
    };
    ['business-account']?: {
      key: string;
      name: string;
      freeTrial: boolean;
      plan: string;
      license: string;
    };
  };
}

const LaunchDarklyProvider: React.FC<{ children: JSX.Element }> = ({ children }) => {
  // The provider and the uaa_id it was initialized with are ONE state value, on
  // purpose. As two they are two separate updates after an `await`, and React 17
  // does not batch outside its own event handlers -- the provider would commit
  // first with no identity recorded, mounting the app subtree (and firing its
  // mount effects, which is where the dashboard's first requests come from)
  // before the identity arrived.
  //
  // `userKey` still lags `user.uaa_id` across an identity change, because the
  // provider is only swapped once `asyncWithLDProvider` resolves, and stays
  // behind permanently if that initialization fails. Consumers that must not act
  // on another identity's flags compare the two; see HeartbeatsV2FlagSync.
  const [ld, setLd] = useState<{ Provider; userKey: string } | null>(null);
  const { user, account } = useContext(SessionContext);

  const buildLaunchDarklyContext = (user): LaunchDarklyProviderProps['context'] => {
    if (!user?.uaa_id) return null;
    const {
      uaa_data: {
        profile: {
          data: { first_name, last_name },
        },
      },
    } = user;

    const context: LaunchDarklyProviderProps['context'] = {
      kind: 'multi',
      user: {
        key: user.uaa_id,
        name: `${first_name} ${last_name}`,
        email: user.email,
      },
    };

    if (account?.id) {
      context['business-account'] = {
        key: account.id,
        name: account.name,
        plan: account.pricing_plan,
        license: account.license_type,
        freeTrial: account.is_free_trial,
      };
    }

    return context;
  };

  useEffect(() => {
    let mounted = true;

    const initLD = async () => {
      if (!user?.uaa_id) return;

      const context = buildLaunchDarklyContext(user);
      if (!context) return;

      try {
        const provider = await asyncWithLDProvider({
          clientSideID: process.env.REACT_APP_DARKLY_CLIENT_SIDE_KEY,
          context,
        });

        if (mounted) {
          setLd({ Provider: provider, userKey: user.uaa_id });
        }
      } catch (error) {
        Notification({
          type: 'error',
          text: 'LaunchDarkly error',
          description: 'There was an error initializing LaunchDarkly',
        });
      }
    };

    initLD();

    return () => {
      mounted = false;
    };
  }, [user?.uaa_id]);

  if (!ld) return <AppReadyLoading />;

  const { Provider: LDProvider, userKey: ldUserKey } = ld;

  return (
    <LDProvider>
      <HeartbeatsV2FlagSync ldUserKey={ldUserKey} sessionUserKey={user?.uaa_id}>
        {children}
      </HeartbeatsV2FlagSync>
    </LDProvider>
  );
};

export default LaunchDarklyProvider;
